/**
 * VideoRecorderUI - Orchestrates RecordButton and VideoPreview
 * 
 * Uses useRecording hook for state management.
 * RecordButton is now a presentational component.
 */

import { useCallback } from "react";
import { Fade } from "@core/components/Transitions/Fade";
import { RecordButton } from "@core/components/Media/RecordButton";
import { VideoPreview } from "@core/components/Media/VideoPreview";
import { useRecording } from "@core/hooks/useRecording";

export interface VideoRecorderUIProps {
  /** Max recording duration in ms (default: 15000) */
  maxDuration?: number;
  /** Use shrink animation on record button (default: false) */
  shrink?: boolean;
  /** Record button size in pixels */
  buttonSize?: number;
  /** Share title for the video */
  shareTitle?: string;
  /** Share text for the video */
  shareText?: string;
  /** Called when recording starts */
  onRecordingStart?: () => void;
  /** Called when recording is requested (before permission prompt resolves) */
  onRecordingRequest?: () => void;
  /** Called when recording stops */
  onRecordingStop?: () => void;
  /** Called when preview is closed */
  onPreviewClose?: () => void;
  /** Called on recording error */
  onError?: (error: string) => void;
  /** Custom className for container */
  className?: string;
  /** Position of record button: 'bottom-center' (default), 'bottom-right', or custom */
  buttonPosition?: 'bottom-center' | 'bottom-right' | string;
}

export function VideoRecorderUI({
  maxDuration = 15000,
  shrink = false,
  buttonSize,
  shareTitle = 'AR Experience',
  shareText = 'Check out this AR experience!',
  onRecordingStart,
  onRecordingRequest,
  onRecordingStop,
  onPreviewClose,
  onError,
  className = '',
  buttonPosition = 'bottom-center',
}: VideoRecorderUIProps) {
  
  const recording = useRecording({
    maxDuration,
    onRecordingStart,
    onRecordingStop,
    onError,
  });

  // Handle record button click
  const handleRecordClick = useCallback(() => {
    if (recording.isRecording) {
      recording.stopRecording();
    } else if (recording.isIdle) {
      onRecordingRequest?.();
      recording.startRecording();
    }
  }, [recording, onRecordingRequest]);

  // Handle preview close
  const handlePreviewClose = useCallback(() => {
    recording.closePreview();
    onPreviewClose?.();
  }, [recording, onPreviewClose]);

  // Get button position classes
  const getButtonPositionClasses = () => {
    switch (buttonPosition) {
      case 'bottom-center':
        return 'absolute bottom-6 left-1/2 -translate-x-1/2';
      case 'bottom-right':
        return 'absolute bottom-6 right-6';
      default:
        return buttonPosition; // Allow custom classes
    }
  };

  const showRecordButton = recording.isIdle || recording.isRecording || recording.isProcessing;
  const showPreview = recording.isPreview && recording.videoUrl;

  return (
    <div className={`fixed inset-0 pointer-events-none z-50 ${className}`}>
      {/* Record Button */}
      <Fade visible={showRecordButton}>
        <div className={`${getButtonPositionClasses()} z-50 pointer-events-auto`}>
          <RecordButton
            isRecording={recording.isRecording}
            isProcessing={recording.isProcessing}
            progress={recording.progress}
            onClick={handleRecordClick}
            shrink={shrink}
            size={buttonSize}
          />
        </div>
      </Fade>

      {/* Video Preview */}
      <Fade visible={!!showPreview}>
        <VideoPreview
          videoUrl={recording.videoUrl}
          onClose={handlePreviewClose}
          shareTitle={shareTitle}
          shareText={shareText}
        />
      </Fade>
    </div>
  );
}

export default VideoRecorderUI;
